import { useState } from 'preact/hooks';
import { api, baixarBlob, mensagemDoErro, salvarBlob } from '../api';
import { inteiro, plural } from '../format';
import type { FonteDadosInfo, ResultadoPlanilha } from '../types';
import { ErroForm, Modal, avisar } from '../ui';
import { lerArquivoTexto } from './ImportarCsvModal';

const MAX_BYTES = 4_000_000;
const CABECALHO = 'codigo_ibge;municipio;indicador;periodo;valor';

type Props = { fonte: FonteDadosInfo; onFechar: () => void; onImportado: () => void };

/** Envio de planilha de uma fonte sem API (IDEB, segurança…): baixa o modelo, valida, mostra o que acontece e só então grava. */
export function ImportarDadosModal({ fonte, onFechar, onImportado }: Props) {
  const enviaveis = (fonte.indicadoresDaFonte ?? []).filter((i) => !i.calculado);
  const [indicador, setIndicador] = useState(enviaveis[0]?.slug ?? '');
  const [ano, setAno] = useState(String(new Date().getFullYear() - 1));
  const [csv, setCsv] = useState('');
  const [arquivo, setArquivo] = useState('');
  const [resultado, setResultado] = useState<ResultadoPlanilha | null>(null);
  const [ocupado, setOcupado] = useState<'' | 'validando' | 'gravando' | 'modelo'>('');
  const [erro, setErro] = useState('');

  const nomeInd = (slug: string) => fonte.indicadoresDaFonte?.find((i) => i.slug === slug)?.nome ?? slug;
  const anoOk = /^\d{4}$/.test(ano);

  async function baixarModelo() {
    setErro('');
    setOcupado('modelo');
    try {
      const blob = await baixarBlob(`/dados-oficiais/fontes/${fonte.slug}/modelo?indicador=${indicador}&periodo=${ano}`);
      salvarBlob(blob, `modelo-${indicador}-${ano}.csv`);
    } catch (e) {
      setErro(mensagemDoErro(e));
    } finally {
      setOcupado('');
    }
  }

  async function aoEscolherArquivo(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const arq = input.files?.[0];
    if (!arq) return;
    setErro('');
    setResultado(null);
    if (arq.size > MAX_BYTES) {
      input.value = '';
      return setErro('O arquivo passa de 4 MB. Divida em partes.');
    }
    setCsv(await lerArquivoTexto(arq));
    setArquivo(arq.name);
  }

  async function enviar(confirmar: boolean) {
    setErro('');
    setOcupado(confirmar ? 'gravando' : 'validando');
    try {
      const r = await api<ResultadoPlanilha>(`/dados-oficiais/fontes/${fonte.slug}/importar`, { method: 'POST', body: { csv, confirmar, ...(arquivo ? { arquivo } : {}) } });
      setResultado(r);
      if (confirmar) {
        avisar(`${plural(r.gravadas ?? r.validas, 'valor gravado', 'valores gravados')}.`);
        onImportado();
        onFechar();
      }
    } catch (e) {
      setErro(mensagemDoErro(e));
    } finally {
      setOcupado('');
    }
  }

  const pronto = resultado !== null && resultado.comErro === 0 && resultado.validas > 0;

  return (
    <Modal titulo={`Enviar planilha: ${fonte.nome.split(' — ')[0]}`} onFechar={onFechar} largo>
      <div class="form">
        <p class="dica">
          1. Escolha o indicador e o ano e baixe o <strong>modelo</strong>, que já vem com os 223 municípios. 2. Preencha a coluna <code>valor</code> (municípios sem dado podem ficar em branco).
          3. Envie o arquivo, confira o que o sistema entendeu e só então grave. Decimais com vírgula (7,5). Colunas aceitas: <code>{CABECALHO}</code>.
        </p>

        <div class="linha-form alinhar">
          <label class="campo-form">
            <span>Indicador</span>
            <select value={indicador} onChange={(e) => setIndicador(e.currentTarget.value)}>
              {enviaveis.map((i) => <option key={i.slug} value={i.slug}>{i.nome} ({i.unidade})</option>)}
            </select>
          </label>
          <label class="campo-form meio">
            <span>Ano</span>
            <input type="number" min={1990} max={new Date().getFullYear() + 1} value={ano} onInput={(e) => setAno(e.currentTarget.value)} />
          </label>
          <button type="button" class="botao" disabled={!anoOk || !indicador || ocupado !== ''} onClick={() => void baixarModelo()}>
            {ocupado === 'modelo' ? 'Baixando…' : 'Baixar modelo'}
          </button>
        </div>
        {(fonte.indicadoresDaFonte ?? []).some((i) => i.calculado) && (
          <p class="dica">
            {(fonte.indicadoresDaFonte ?? []).filter((i) => i.calculado).map((i) => i.nome).join(', ')}: calculado pelo sistema a partir da contagem e da população do Censo. Não precisa enviar.
          </p>
        )}

        <div class="linha-form alinhar">
          <label class="botao arquivo">
            Escolher arquivo CSV
            <input type="file" accept=".csv,text/csv" onChange={aoEscolherArquivo} class="so-leitor-foco" />
          </label>
          {arquivo && <span class="dica">{arquivo}</span>}
        </div>

        <label class="campo-form">
          <span>Ou cole o conteúdo aqui</span>
          <textarea rows={6} value={csv} class="mono" spellcheck={false} placeholder={CABECALHO}
            onInput={(e) => { setCsv(e.currentTarget.value); setArquivo(''); setResultado(null); }} />
        </label>

        <ErroForm texto={erro} />

        {resultado && (
          <section class="resultado-import" aria-live="polite">
            <p>
              <strong>{plural(resultado.total, 'linha lida', 'linhas lidas')}:</strong>{' '}
              <span class="verde-txt">{resultado.validas} com valor válido</span>
              {resultado.vazias > 0 && <>, {resultado.vazias} em branco (ignoradas)</>}
              {resultado.comErro > 0 && <>, <span class="vermelho-txt">{resultado.comErro} com problema</span></>}.
            </p>
            {resultado.porIndicador.length > 0 && (
              <ul class="lista-simples">
                {resultado.porIndicador.map((p) => <li key={p.indicador + p.periodo}>{nomeInd(p.indicador)}, {p.periodo}: {plural(p.municipios, 'município', 'municípios')} de 223</li>)}
                {resultado.taxasCalculadas > 0 && <li>{plural(resultado.taxasCalculadas, 'taxa calculada', 'taxas calculadas')} pelo sistema (por 100 mil habitantes)</li>}
              </ul>
            )}
            {resultado.sobrescreve > 0 && <p class="aviso">{plural(resultado.sobrescreve, 'valor já existe', 'valores já existem')} para este ano e será substituído.</p>}
            {resultado.avisos.map((a) => <p key={a} class="aviso">{a}</p>)}
            {resultado.comErro > 0 && (
              <>
                <p class="dica">Nada é gravado enquanto houver linha com problema. Corrija o arquivo e valide de novo.</p>
                <div class="tabela-rolagem pequena">
                  <table class="tabela">
                    <thead><tr><th scope="col">Linha</th><th scope="col">Problema</th></tr></thead>
                    <tbody>
                      {resultado.erros.map((er, i) => (
                        <tr key={`${er.linha}-${i}`}><td>{er.linha || '—'}</td><td>{er.mensagens.length ? er.mensagens.join(' · ') : 'Linha inválida'}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {resultado.errosOcultos > 0 && <p class="dica">E mais {inteiro(resultado.errosOcultos)} linhas com problema. Corrija estas primeiro.</p>}
              </>
            )}
          </section>
        )}

        <div class="modal-acoes">
          <button type="button" class="botao" onClick={onFechar}>Cancelar</button>
          <button type="button" class="botao" disabled={!csv.trim() || ocupado !== ''} onClick={() => void enviar(false)}>
            {ocupado === 'validando' ? 'Validando…' : 'Validar'}
          </button>
          <button type="button" class="botao primario" disabled={!pronto || ocupado !== ''} onClick={() => void enviar(true)}>
            {ocupado === 'gravando' ? 'Gravando…' : pronto ? `Gravar ${plural(resultado!.validas, 'valor', 'valores')}` : 'Gravar'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
